/**
 * SettingRow · SettingsSection — Dashfolio 설정 패널 핵심 패턴
 *
 * 기준: docs/design/dashfolio-dna.md §3.2 "설정 행"
 *
 * 구조:
 *   <SettingsSection title="…" description="…">
 *     <SettingRow icon={…} label="…" description="…" control={<Toggle/>} />
 *     <SettingRow label="…" control={<Slider/>} />
 *   </SettingsSection>
 *
 * 규칙:
 *  - SettingsSection 은 카드 shell (surface + hairline + radius-lg + 24px padding)
 *  - SettingRow 들은 내부에서 hairline-b 로 구분 (첫 row 제외, `[&>*+*]:border-t` 트릭)
 *  - Left: 아이콘 16px + label (13px medium) + optional description (12px secondary)
 *  - Right: control 슬롯 (caller 가 element 주입)
 *  - layout='stacked' 시 긴 텍스트 필드용으로 control 을 label 아래 배치
 *
 * 접근성:
 *  - label 에 `id` 자동 생성 → caller control 에서 `aria-labelledby` 연결용
 *  - `useId` 로 충돌 없는 고유 id (React 19 표준)
 */

import type { ComponentType, HTMLAttributes, ReactNode, SVGProps } from 'react';
import { useId } from 'react';
import { cn } from '../utils/cn';

/**
 * lucide-react 의 `LucideIcon` 과 구조 호환되는 로컬 타입.
 * `@lashpic/ui` 에 lucide-react 를 peer dep 으로 끌어오지 않기 위함.
 * caller 쪽(admin) 이 lucide-react 를 사용하더라도 타입 할당 가능.
 */
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/* ─────────────────────────────────────────────────────────────
 * SettingsSection — 섹션 카드 래퍼
 * ───────────────────────────────────────────────────────────── */

interface SettingsSectionProps extends HTMLAttributes<HTMLElement> {
  /** 섹션 제목 (18px semibold · tight tracking) */
  title: string;
  /** 제목 아래 13px secondary 설명 */
  description?: string;
  /** SettingRow 자식들 — 내부에서 hairline-b 자동 구분 */
  children: ReactNode;
  /** 추가 className (카드 shell) */
  className?: string;
}

export function SettingsSection({
  title,
  description,
  children,
  className,
  ...rest
}: SettingsSectionProps) {
  return (
    <section
      className={cn(
        'rounded-[var(--radius-admin-lg)]',
        'border border-[var(--color-admin-hairline)]',
        'bg-[var(--color-admin-surface)]',
        'p-6',
        className,
      )}
      {...rest}
    >
      <header className="pb-4">
        <h2 className="text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-admin-text)]">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-[13px] leading-[1.5] text-[var(--color-admin-text-secondary)]">
            {description}
          </p>
        ) : null}
      </header>

      {/*
        행 구분선: 첫 행에는 top border 없음, 이후 행부터 `[&>*+*]:border-t`
        border-t 은 hairline 색 — border-[var(--color-admin-hairline)]
      */}
      <div
        className={cn(
          'border-t border-[var(--color-admin-hairline)]',
          '[&>*+*]:border-t [&>*+*]:border-[var(--color-admin-hairline)]',
        )}
      >
        {children}
      </div>
    </section>
  );
}

SettingsSection.displayName = 'SettingsSection';

/* ─────────────────────────────────────────────────────────────
 * SettingRow — 라벨 + 컨트롤 행
 * ───────────────────────────────────────────────────────────── */

type SettingRowLayout = 'split' | 'stacked';

interface SettingRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 좌측 아이콘 (16px, tertiary 색) — lucide-react `LucideIcon` 호환 */
  icon?: IconComponent;
  /** 라벨 텍스트 (13px medium · primary) */
  label: string;
  /** 라벨 아래 12px secondary 설명 */
  description?: string;
  /** 우측 컨트롤 슬롯 — caller 가 Toggle/Slider/Input 등 주입 */
  control: ReactNode;
  /**
   * 레이아웃:
   *  - 'split' (기본): 좌측 라벨 · 우측 컨트롤
   *  - 'stacked': 라벨 아래 컨트롤 (긴 입력 필드용)
   */
  layout?: SettingRowLayout;
  /** 라벨 element id passthrough — control 쪽 aria-labelledby 용 */
  labelId?: string;
  /** 추가 className */
  className?: string;
}

export function SettingRow({
  icon: Icon,
  label,
  description,
  control,
  layout = 'split',
  labelId,
  className,
  ...rest
}: SettingRowProps) {
  const autoId = useId();
  const resolvedLabelId = labelId ?? `setting-${autoId}`;
  const descriptionId = description ? `${resolvedLabelId}-desc` : undefined;

  return (
    <div
      className={cn(
        'flex min-h-[56px] gap-4 px-0 py-3.5',
        layout === 'split' ? 'items-start justify-between' : 'flex-col items-stretch',
        className,
      )}
      {...rest}
    >
      {/* Left: icon + label + description */}
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {Icon ? (
          <Icon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-[var(--color-admin-text-tertiary)]"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            id={resolvedLabelId}
            className="text-[13px] font-medium leading-[1.35] text-[var(--color-admin-text)]"
          >
            {label}
          </span>
          {description ? (
            <span
              id={descriptionId}
              className="mt-0.5 text-[12px] leading-[1.45] text-[var(--color-admin-text-secondary)]"
            >
              {description}
            </span>
          ) : null}
        </div>
      </div>

      {/* Right: control slot */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-2',
          layout === 'split' ? 'justify-end' : 'mt-3 justify-start',
        )}
      >
        {control}
      </div>
    </div>
  );
}

SettingRow.displayName = 'SettingRow';
